import type {
  PaigeDestination,
  PaigeDestinationInput,
  PaigeDestinationRemoved,
  PaigeDestinationsBridge,
  PaigeDestinationTest,
  PaigeFilingPreview,
  PaigeStorageEntry,
  PaigeStorageProvider,
} from './paige.global';
import { responseWords } from './problem-words';
import { productDestinationChannel } from './product-destination-channel';

/** One call on the job's channel (`{engineOrigin}/destinations...`); rejects with the API's reason. */
async function call<T>(
  engineOrigin: string,
  tenant: string | undefined,
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  // `~` paths are the API's own, outside the job: `/v1/destinations/storage`.
  const url = path.startsWith('~')
    ? `${engineOrigin.replace(/\/jobs\/[^/]+$/, '')}/destinations${path.slice(1)}`
    : `${engineOrigin}/destinations${path}`;
  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(method === 'GET' ? {} : { 'Idempotency-Key': crypto.randomUUID() }),
      ...(tenant ? { 'Paige-Tenant': tenant } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) throw new Error(await reasonOf(response));
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/** The problem's detail when the API wrote one for a person; otherwise Paige's plain line (problem-words.ts). */
export function reasonOf(response: Response): Promise<string> {
  return responseWords(response);
}

/** The destinations bridge over the job's channel. */
export function destinationChannel(
  engineOrigin: string,
  tenant: string | undefined,
): PaigeDestinationsBridge {
  return {
    list: () => call<PaigeDestination[]>(engineOrigin, tenant, '', 'GET'),
    save: (input: PaigeDestinationInput, id?: string) =>
      id
        ? call<PaigeDestination>(engineOrigin, tenant, `/${id}`, 'PUT', input)
        : call<PaigeDestination>(engineOrigin, tenant, '', 'POST', input),
    remove: (id: string, sendTo?: string) =>
      call<PaigeDestinationRemoved | undefined>(
        engineOrigin,
        tenant,
        sendTo ? `/${id}?sendTo=${encodeURIComponent(sendTo)}` : `/${id}`,
        'DELETE',
      ),
    test: (id: string) => call<PaigeDestinationTest>(engineOrigin, tenant, `/${id}/test`, 'POST'),
    retry: (id: string) => call<PaigeDestination>(engineOrigin, tenant, `/${id}/retry`, 'POST'),
    resetHostKey: (id: string) =>
      call<PaigeDestination>(engineOrigin, tenant, `/${id}/host-key`, 'DELETE'),
    backfillCount: async (id: string) =>
      (await call<{ documents: number }>(engineOrigin, tenant, `/${id}/backfill`, 'GET')).documents,
    backfill: async (id: string) =>
      (await call<{ documents: number }>(engineOrigin, tenant, `/${id}/backfill`, 'POST'))
        .documents,
    ...storageChannel(engineOrigin, tenant),
    ...productDestinationChannel((path, method, body) =>
      call(engineOrigin, tenant, path, method, body),
    ),
  };
}

/** The storage destinations' calls (ADR-0016): providers, connect, disconnect, the picker, New folder and the preview. */
function storageChannel(engineOrigin: string, tenant: string | undefined) {
  return {
    storageProviders: () => call<PaigeStorageProvider[]>(engineOrigin, tenant, '~/storage', 'GET'),
    connect: async (id: string, returnTo: string) =>
      (
        await call<{ authorizeUrl: string }>(engineOrigin, tenant, `/${id}/connect`, 'POST', {
          returnTo,
        })
      ).authorizeUrl,
    disconnect: (id: string) =>
      call<PaigeDestination>(engineOrigin, tenant, `/${id}/connection`, 'DELETE'),
    locations: async (id: string, parent?: string, query?: string) => {
      const search = new URLSearchParams({
        ...(parent ? { parent } : {}),
        ...(query ? { q: query } : {}),
      });
      const suffix = search.size > 0 ? `?${search.toString()}` : '';
      return (
        await call<{ entries: PaigeStorageEntry[] }>(
          engineOrigin,
          tenant,
          `/${id}/locations${suffix}`,
          'GET',
        )
      ).entries;
    },
    createFolder: (id: string, parent: string, name: string) =>
      call<PaigeStorageEntry>(engineOrigin, tenant, `/${id}/locations`, 'POST', { parent, name }),
    preview: (kind: string, locationPath?: string) =>
      call<PaigeFilingPreview>(engineOrigin, tenant, '/filing-preview', 'POST', {
        kind,
        ...(locationPath ? { locationPath } : {}),
      }),
  };
}
